import { createHash } from "crypto"

import { ANALYTIC_DEBUG_SHOW_LOG, IS_PRODUCTION } from "../env-management"
import { loggingGroup } from "../infrastructure/debug-util"
import { SentryWrapper } from "../ThirdParty/Sentry/SentryWrapper"
import { isBrowser } from "../infrastructure/ssg-utils"
import { isObject } from "../utils"

import { ymSendHit } from "./yandex-metrika"
import { gaSendEvent } from "./google-analytics"
import { mindboxOperationTrack } from "./mindbox"
import { FbPixel } from "./facebook-pixel"
import { tmrSendHitGoal } from "./mailru-pixel"

const UNKNOWN_META_ID = "unknown meta id"
const UNKNOWN_DESC_ID = "unknown eventDesc id"

export interface EventMeta {
    title?: string
    format?: string
    id?: string
    event_label?: string | boolean
    event_value?: string | boolean
    data_layer_variable?: boolean
    ga_id?: string | string[]
    fb_content_id?: boolean

    compute(d: unknown, hit?: EventDesc): number | string | undefined | Record<string, unknown>
}

function wrapCompute(
    key: string,
    meta: EventMeta,
    compute_method: (
        d: EventDesc,
        hit?: EventDesc
    ) => number | string | undefined | Record<string, unknown>
) {
    return function (d: EventDesc, hit?: EventDesc) {
        let proxy = d
        // Throw on missing properties
        if (isBrowser() && d && typeof d === "object") {
            proxy = new window.Proxy(d, {
                get(target: Record<string | symbol, unknown>, p) {
                    if (meta.data_layer_variable) {
                        return target[p]
                    }
                    if (!(p in target)) {
                        console.error("Target:", target)
                        if (hit) {
                            throw new Error(
                                `Missing property '${String(p)}' for Analytics.${
                                    hit.id ?? UNKNOWN_DESC_ID
                                }`
                            )
                        }
                    }
                    return target[p]
                },
                has(target, p) {
                    return (target && p in target) || false
                },
            })
        }
        try {
            return compute_method(proxy, hit)
        } catch (e) {
            console.error(
                `Exception while computing attribute ${meta.id ?? UNKNOWN_META_ID}`,
                "arguments",
                {
                    proxy,
                    hit,
                }
            )
            console.error(e)
            SentryWrapper.getInstance().captureException(e, { extra: { eventKey: key } })
        }
    }
}

export function metadata(metas: Record<string, EventMeta>) {
    for (const key of Object.keys(metas)) {
        const meta = metas[key]
        meta.id = key
        const compute_method = meta.compute.bind(meta)
        if (IS_PRODUCTION) {
            meta.compute = (d, hit) => {
                // Never throw
                try {
                    return compute_method(d, hit)
                } catch (e) {
                    SentryWrapper.getInstance().captureException(e, { extra: { eventKey: key } })
                    return undefined
                }
            }
        } else {
            meta.compute = wrapCompute(key, meta, compute_method)
        }
    }
    return metas
}

function delegate(newId: string, meta: EventMeta) {
    return {
        ga_id: newId,
        delegate: meta,
        compute: function (...args: [d: Record<string, unknown>, hit?: EventDesc]) {
            return this.delegate.compute(...args)
        },
    }
}

export function digest(newId: string, ...meta: EventMeta[]) {
    return {
        title: meta.map((m) => m.id).join("&"),
        ga_id: newId,
        digestFor: meta,
        compute: function (...args: [d: Record<string, unknown>, hit?: EventDesc]) {
            return this.digestFor
                .map((m) => {
                    const compute = m.compute(...args)
                    const computeString =
                        (!isObject(compute) ? compute : "undefined") ?? "undefined"
                    return `${m.id ? m.id : UNKNOWN_META_ID}=${computeString}`
                })
                .join("&")
        },
    }
}

// TODO: GA-specific
export function eventLabel(meta: EventMeta) {
    return delegate("eventLabel", meta)
}

/*
nonInteraction parameters (from GA help):
  Determines how you want bounce rate defined for pages on your site that also include event tracking.
  Bounce is defined as a session containing only one interaction hit.
  A session containing a single page tagged with non-interaction events is counted as a bounce—even
  if the visitor also triggers the event during the session

  https://support.google.com/analytics/answer/1033068#NonInteractionEvents
 */

// TODO: GA-specific
export function nonInteraction(extras: Record<string, unknown>) {
    return event(true, extras)
}

// TODO: GA-specific
export function interaction(extras: Record<string, unknown>) {
    return event(false, extras)
}

function event(nonInteraction: boolean, extras: Record<string, unknown>) {
    return {
        type: "event",
        // TODO: GA-specific
        nonInteraction,
        ...extras,
    }
}

// TODO: GA-specific
export function pageview(extras: Record<string, unknown>) {
    return {
        type: "pageview",
        ...extras,
    }
}

// Safe proxy to avoid failing in production when a bug is related purely to reporting
function safeProxy(target: Record<string, unknown>) {
    if (!isBrowser()) return target
    return new window.Proxy(target, {
        get: (target, propertyKey) => {
            const original = target[String(propertyKey)]
            if (!IS_PRODUCTION) {
                // react creates its own properties on objects, and we don't care about them
                if (
                    original === undefined &&
                    typeof propertyKey === "string" &&
                    !propertyKey.startsWith("_") &&
                    !propertyKey.startsWith("$$")
                )
                    throw new Error(
                        `Missing event description: ${propertyKey} in the Analytics object`
                    )
                return original
            }
            return (
                original ||
                function (...args: unknown[]) {
                    SentryWrapper.getInstance().captureException(
                        `Analytics, unknown event name: ${String(propertyKey)}`,
                        { extra: { args: args.join(", ") } }
                    )
                }
            )
        },
    })
}

export interface EventDesc {
    fb?: FbPixel
    id?: string
    title?: string
    explanation?: string[]
    data?: EventMeta[]
    category?: string
    action?: string
    uriSuffix?: string
    type?: string
    defaultData?: EventMeta[]
    nonInteraction?: boolean
}

export interface ReadyEventDesc extends EventDesc {
    (data?: object): void

    eventDesc: EventDesc
}

export function setUpAnalyticsObject(
    analytics: Record<string, EventDesc>,
    defaultData: EventMeta[]
): Record<string, ReadyEventDesc> {
    for (const eventId of Object.keys(analytics)) {
        const eventDesc = analytics[eventId]
        eventDesc.id = eventId
        if (eventDesc.explanation && !Array.isArray(eventDesc.explanation)) {
            eventDesc.explanation = [eventDesc.explanation]
        }
        if (eventDesc.data && !Array.isArray(eventDesc.data)) {
            eventDesc.data = [eventDesc.data]
        }
        eventDesc.defaultData = defaultData
        // TODO: GA-specific
        if (!eventDesc.action) {
            eventDesc.action = eventId
        }
        // TODO: GA-specific
        eventDesc.category = eventDesc.action
        // TODO: GA-specific
        if (eventDesc.type === "pageview" && !eventDesc.uriSuffix) {
            eventDesc.uriSuffix = `/${eventId}`
        }

        const eventSender = function (data: Record<string, unknown>) {
            loggingGroup("Event: " + eventId, () => {
                sendDataToAnalyticsSystems(eventDesc, data)
            })
        }
        analytics[eventId] = eventSender as ReadyEventDesc
        // Used in FunnelDocs.js
        eventSender.eventDesc = eventDesc
    }
    return safeProxy(analytics) as Record<string, ReadyEventDesc>
}

function toGAEventParams(data: Record<string, unknown>): Record<string, string> {
    return Object.fromEntries(Object.entries(data).map(([key, value]) => [key, String(value)]))
}

function sendDataToAnalyticsSystems(eventDesc: EventDesc, data: Record<string, unknown>) {
    if (!eventDesc.action) return
    const fields = extractDataFields(eventDesc, data)
    dataLayerEvent(eventDesc.action, { label: fields.eventLabel, ...fields })
    // TODO: fix FB
    trackInFb(eventDesc, fields)
    ymSendHit(`/${eventDesc.id ? eventDesc.id : UNKNOWN_DESC_ID}`, fields)
    gaSendEvent(
        eventDesc.category,
        eventDesc.action,
        fields.eventLabel?.toString(),
        fields.eventValue?.toString(),
        toGAEventParams(fields)
    )

    eventDesc.id && mindboxOperationTrack(eventDesc.id, data)
    tmrSendHitGoal.filter(eventDesc.id) && tmrSendHitGoal(eventDesc.id)
}

function patchGAspecific(
    fields: Record<string, string | number | undefined | Record<string, unknown>>,
    eventDescription: EventDesc,
    meta: EventMeta,
    value: string | number | undefined | Record<string, unknown>
) {
    if (eventDescription.type === "event") {
        if (meta.event_label && !isObject(value)) {
            fields.eventLabel = value
        }
        if (meta.event_value && !isObject(value)) {
            fields.eventValue = value
        }
        if (meta.data_layer_variable) {
            fields.dataLayerVariable = value
        }
        if (meta.id) {
            fields[meta.id] = value
        }
    }
}

function patchFBspecific(
    fields: Record<string, string | number | undefined | Record<string, unknown>>,
    eventDescription: EventDesc,
    meta: EventMeta,
    value: string | number | undefined | Record<string, unknown>
) {
    if (eventDescription.fb && eventDescription.fb.addFields) {
        eventDescription.fb.addFields(fields, meta, value)
    }
}

function extractDataFields(eventDescription: EventDesc, data: Record<string, unknown>) {
    if (!eventDescription.data && !eventDescription.defaultData) return {}
    const allMetadata = (eventDescription.data || []).concat(eventDescription.defaultData || [])
    const fields: Record<string, string | number | undefined | Record<string, unknown>> = {}
    for (const meta of allMetadata) {
        const value = meta.compute(data, eventDescription)
        if (value === undefined) continue
        patchGAspecific(fields, eventDescription, meta, value)
        patchFBspecific(fields, eventDescription, meta, value)
    }
    return fields
}

// TODO: fb-specific
function trackInFb(hit: EventDesc, parameters: Record<string, unknown>) {
    if (hit.fb) {
        hit.fb.trackEvent(hit, parameters)
    }
}

export function accessCodeAuthorized(searchStr: string) {
    const match = searchStr.match(/access_code=(\d+)/)
    if (match) {
        const code = match[1]
        const hash = createHash("sha1").update(code).digest("hex")
        return hash === "527bac8fe59ac55c66dd64356c673e65e0ab73ba"
    }
}

function logMessage(message: string, formatter = (message: string) => message, styles?: unknown) {
    if (ANALYTIC_DEBUG_SHOW_LOG) {
        console.log(formatter(message), styles)
    }
}

export function dataLayerEvent(name: string, props?: Record<string, unknown>) {
    const dataLayer = window.dataLayer
    const event = {
        event: name,
        ...props,
    }
    if (!dataLayer) {
        logMessage(
            `dataLayer.push(${JSON.stringify(event, null, 2)})`,
            (message) => `%c${message}`,
            "background: darkgrey; color: yellow"
        )
    }
    if (dataLayer) {
        dataLayer.push(event)
        logMessage(
            `dataLayer.push(${JSON.stringify(event, null, 2)})`,
            (message) => `%c${message}`,
            "background: darkgrey; color: yellow"
        )
    }
}
